import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Componentes 

// Funciones para obtener datos
import ObtenerPais from '../functions/ObtenerPais';
import GuardarProvincia from '../functions/GuardarProvincia';
import ObtenerProvincia from '../functions/ObtenerProvincia';
import InsertarLocalidad from '../functions/InsertarLocalidad';
import ObtenerLocalidad from '../functions/ObtenerLocalidad';
import GuardarLocalidad from '../functions/GuardarLocalidad';

// Tipos de datos
import PaisEditar from '../types/PaisEditar';
import ProvinciaTabla from '../types/ProvinciaTabla';
import LocalidadTabla from '../types/LocalidadTabla';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarLocalidadProps {
    idPais: number;
    idProvincia: number;
    idLocalidad: number;
    guardarCambios: (localidad: LocalidadTabla) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}

export const EditarLocalidad: React.FC<EditarLocalidadProps> = ({ idPais, idProvincia, idLocalidad, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [pais, setPais] = useState<PaisEditar | null>(null);
    const [provincia, setProvincia] = useState<ProvinciaTabla | null>(null);
    const [localidad, setLocalidad] = useState<LocalidadTabla | null>(null);

    const [errorPais, setErrorPais] = useState<string | null>(null);
    const [errorProvincia, setErrorProvincia] = useState<string | null>(null);
    const [errorLocalidad, setErrorLocalidad] = useState<string | null>(null);

    const [localCodPostal, setLocalCodPostal] = useState('');
    const [localLocalidad, setLocalLocalidad] = useState('');
    const [localProvincia, setLocalProvincia] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localIdPais, setLocalIdPais] = useState(0);
    const [localIdProvincia, setLocalIdProvincia] = useState(0);
    
    useEffect(() => {
        
        const fetchDatos = async () => {
            try {
                
                const dataPais = await ObtenerPais(idPais);

                if (dataPais.pais_ok === 'si') {
                    // Todo ok, pais recuperado
                    const paisRecuperado: PaisEditar = {
                        id: dataPais.pais[0].id,
                        pais: dataPais.pais[0].nombre,
                        codigo_iso: dataPais.pais[0].codigo_iso,
                        codigo_tel: dataPais.pais[0].codigo_telefonico,
                        habilitado: dataPais.pais[0].habilitado,
                        fch_crea: dataPais.pais[0].fecha_creacion,
                        fch_modi: dataPais.pais[0].fecha_modificacion,
                        dado_baja: dataPais.pais[0].dado_de_baja,
                        fch_baja: dataPais.pais[0].fecha_baja
                    };
                    
                    setPais(paisRecuperado);

                    const dataProvincia = await ObtenerProvincia(idProvincia);

                    if (dataProvincia.provincia_ok === 'si') {
                        // Todo ok, provincia recuperada

                        const provinciaRecuperada: ProvinciaTabla = {
                            id: dataProvincia.provincia[0].id,
                            provincia: dataProvincia.provincia[0].provincia,
                            habilitado: dataProvincia.provincia[0].habilitado,
                            fch_crea: dataProvincia.provincia[0].fecha_creacion,
                            fch_modi: dataProvincia.provincia[0].fecha_modificacion,
                            dado_baja: dataProvincia.provincia[0].dado_de_baja,
                            fch_baja: dataProvincia.provincia[0].fecha_baja,
                            pais_id: dataPais.pais[0].id,
                            pais_nombre: dataPais.pais[0].nombre
                        };

                        setProvincia(provinciaRecuperada);

                        const dataLocalidad = await ObtenerLocalidad(idLocalidad);
                        
                        if (dataLocalidad.localidad_ok === 'si') {
                                
                            const localidadRecuperada: LocalidadTabla = {
                                id: dataLocalidad.localidad[0].id,
                                codpostal: dataLocalidad.localidad[0].codpostal,
                                localidad: dataLocalidad.localidad[0].localidad,
                                habilitado: dataLocalidad.localidad[0].habilitado,
                                fch_crea: dataLocalidad.localidad[0].fecha_creacion,
                                fch_modi: dataLocalidad.localidad[0].fecha_modificacion,
                                dado_baja: dataLocalidad.localidad[0].dado_de_baja,
                                fch_baja: dataLocalidad.localidad[0].fecha_baja,
                                pais_id: dataPais.pais[0].id,
                                pais_nombre: dataPais.pais[0].nombre,
                                provincia_id: dataProvincia.provincia[0].id,
                                provincia_nombre: dataProvincia.provincia[0].provincia
                            };

                            setLocalidad(localidadRecuperada);

                            setLocalCodPostal(dataLocalidad.localidad[0].codpostal);
                            setLocalLocalidad(dataLocalidad.localidad[0].localidad);
                            setLocalHabilitado(dataLocalidad.localidad[0].habilitado);
                            setLocalDadoBaja(dataLocalidad.localidad[0].dado_de_baja);
                            setLocalFchCrea(dataLocalidad.localidad[0].fecha_creacion);
                            setLocalFchModi(dataLocalidad.localidad[0].fecha_modificacion);
                            if (dataLocalidad.localidad[0].dado_de_baja === 'N') {
                                setLocalFchBaja('');
                            } else {
                                setLocalFchBaja(dataLocalidad.localidad[0].fecha_baja);
                            }
                            setLocalIdPais(dataPais.pais[0].id);
                            setLocalIdProvincia(dataProvincia.provincia[0].id);

                            setErrorPais(null);
                            setErrorProvincia(null);

                        } else {
                            setErrorLocalidad(dataLocalidad.mensaje);
                            setLocalidad(null);
                        }
                    } else {
                        
                        // Hubo errores al recuperar el pais
                        setErrorPais(dataProvincia.mensaje);
                        setProvincia(null);
                        setLocalCodPostal('');
                        setLocalLocalidad('');
                        setLocalFchCrea('');
                        setLocalFchModi('');
                        setLocalFchBaja('');
                        setLocalHabilitado('');
                        setLocalDadoBaja('');
                        setLocalIdPais(0);
                        setLocalIdProvincia(0);
                    }
                } else {
                    setErrorPais(dataPais.mensaje);
                    setPais(null);
                    setProvincia(null);
                    setLocalCodPostal('');
                    setLocalLocalidad('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalFchBaja('');
                    setLocalHabilitado('');
                    setLocalDadoBaja('');
                    setLocalIdPais(0);
                    setLocalIdProvincia(0);
                }
                setIsLoading(false);
            } catch (err) {
                setErrorPais('Error al obtener la información de la base de datos.');
                setIsLoading(false);
                setPais(null);
                setProvincia(null);
                setLocalCodPostal('');
                setLocalLocalidad('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalHabilitado('');
                setLocalDadoBaja('');
                setLocalIdPais(0);
                setLocalIdProvincia(0);
            }
        };

        const fetchDatosIniciales = async () => {

            try {
                
                const dataPais = await ObtenerPais(idPais);

                if (dataPais.pais_ok === 'si') {
                    // Todo ok, pais recuperado
                    const paisRecuperado: PaisEditar = {
                        id: dataPais.pais[0].id,
                        pais: dataPais.pais[0].nombre,
                        codigo_iso: dataPais.pais[0].codigo_iso,
                        codigo_tel: dataPais.pais[0].codigo_telefonico,
                        habilitado: dataPais.pais[0].habilitado,
                        fch_crea: dataPais.pais[0].fecha_creacion,
                        fch_modi: dataPais.pais[0].fecha_modificacion,
                        dado_baja: dataPais.pais[0].dado_de_baja,
                        fch_baja: dataPais.pais[0].fecha_baja
                    };
                    
                    setPais(paisRecuperado);

                    const dataProvincia = await ObtenerProvincia(idProvincia);

                    if (dataProvincia.provincia_ok === 'si') {
                        // Todo ok, provincia recuperada

                        const provinciaRecuperada: ProvinciaTabla = {
                            id: dataProvincia.provincia[0].id,
                            provincia: dataProvincia.provincia[0].provincia,
                            habilitado: dataProvincia.provincia[0].habilitado,
                            fch_crea: dataProvincia.provincia[0].fecha_creacion,
                            fch_modi: dataProvincia.provincia[0].fecha_modificacion,
                            dado_baja: dataProvincia.provincia[0].dado_de_baja,
                            fch_baja: dataProvincia.provincia[0].fecha_baja,
                            pais_id: dataPais.pais[0].id,
                            pais_nombre: dataPais.pais[0].nombre
                        };

                        setProvincia(provinciaRecuperada);
                        setLocalIdPais(idPais);
                        setLocalIdProvincia(idProvincia);
            
                        setLocalHabilitado('S');
                        setLocalDadoBaja('N');
                        setLocalCodPostal('');
                        setLocalLocalidad('');
                        setLocalFchCrea('');
                        setLocalFchModi('');
                        setLocalFchBaja('');

                        setLocalidad({id: 0, codpostal: '', localidad: '', habilitado: 'S', fch_crea: '', fch_modi: '', dado_baja: 'N', fch_baja: '', pais_id: idPais, pais_nombre: '', provincia_id: idProvincia, provincia_nombre: ''});
            
                        setIsLoading(false);

                    } else {

                    }
                } else {
                    setErrorPais(dataPais.mensaje);
                    setPais(null);
                    setProvincia(null);
                    setLocalCodPostal('');
                    setLocalLocalidad('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalFchBaja('');
                    setLocalHabilitado('');
                    setLocalDadoBaja('');
                    setLocalIdPais(0);
                    setLocalIdProvincia(0);
                }
            } catch (err) {
                setErrorPais('Error al obtener la información de la base de datos.');
                setIsLoading(false);
                setPais(null);
                setProvincia(null);
                setLocalCodPostal('');
                setLocalLocalidad('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalHabilitado('');
                setLocalDadoBaja('');
                setLocalIdPais(0);
                setLocalIdProvincia(0);
            }

        };


        if (idLocalidad > 0) {
            // Estoy editando una localidad
            fetchDatos();

        } else {

            // Estoy creando una nueva localidad
            fetchDatosIniciales();
        }

    }, []);


    const handleChangeLocalCodPostal = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[0-9]{0,6}$/;
        if (pattern.test(event.target.value)) {
            setLocalCodPostal(event.target.value);
        }
    };

    const handleChangeLocalLocalidad = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLocalidad(event.target.value);
    };

    const handleChangeLocalHabitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const guardarCambiosLocal = async () => {

        if (idLocalidad === 0) {
            // Crear nueva localidad

            const LocalidadNueva: LocalidadTabla = {
                id: 0,
                codpostal: localCodPostal,
                localidad: localLocalidad,
                habilitado: localHabilitado,
                fch_crea: '',
                fch_modi: '',
                dado_baja: localDadoBaja,
                fch_baja: '',
                pais_id: pais?.id || 0,
                pais_nombre: pais?.pais || '',
                provincia_id: provincia?.id || 0,
                provincia_nombre: provincia?.provincia || ''
            };

            const respuesta = await InsertarLocalidad(LocalidadNueva);
            
            if (respuesta.localidad_guardada === 'si') {
                guardarCambios(LocalidadNueva);
            } else {
                alert('Error al guardar los cambios: ' + respuesta.mensaje);
            }
            
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica del pais
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);

                // Modificar datos Localidad
                const LocalidadEditada: LocalidadTabla = {
                    id: idLocalidad,
                    codpostal: localCodPostal,
                    localidad: localLocalidad,
                    habilitado: 'N',
                    fch_crea: localFchCrea,
                    fch_modi: guardarFecha,
                    dado_baja: 'S',
                    fch_baja: guardarFecha,
                    pais_id: pais?.id || 0,
                    pais_nombre: pais?.pais || '',
                    provincia_id: provincia?.id || 0,
                    provincia_nombre: provincia?.provincia || ''
                };

                const respuesta = await GuardarLocalidad(LocalidadEditada);

                if (respuesta.localidad_guardada === 'si') {
                    guardarCambios(LocalidadEditada);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Preparo fecha de modificación
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                
                // Modificar datos Localidad
                const LocalidadEditada: LocalidadTabla = {
                    id: idLocalidad,
                    codpostal: localCodPostal,
                    localidad: localLocalidad,
                    habilitado: localHabilitado,
                    fch_crea: localFchCrea,
                    fch_modi: guardarFecha,
                    dado_baja: localDadoBaja,
                    fch_baja: localDadoBaja === 'S' ? guardarFecha : '',
                    pais_id: pais?.id || 0,
                    pais_nombre: pais?.pais || '',
                    provincia_id: provincia?.id || 0,
                    provincia_nombre: provincia?.provincia || ''
                };

                const respuesta = await GuardarLocalidad(LocalidadEditada);

                if (respuesta.localidad_guardada === 'si') {
                    guardarCambios(LocalidadEditada);
                } else {
                    alert('Error al guardar los cambios:' + respuesta.mensaje);
                }
            }
        }
    };

    const handleChangeLocalProvincia = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalProvincia(event.target.value);
    };

    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && provincia) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminando ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Localidad: {localidad?.localidad}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idPais > 0 ? "Editando Localidad: " + localidad?.localidad : "Nueva Localidad"}</h1>
                    )}     
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-3 flex-wrap justify-between items-start">
                        <div className="flex flex-col justify-start items-center max-w-[320px] flex-grow w-full">
                            <TextField id="localidad" label="Localidad" variant="outlined" value={localLocalidad} autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalLocalidad}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[100px] flex-grow w-full">
                            <TextField id="codpostal" label="Cód.Postal" variant="outlined" value={localCodPostal} 
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCodPostal}
                                disabled={eliminando}
                            />
                        </div>
                        {(localidad?.id || 0) > 0 && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabitado}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminando ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};